import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Upload, Search, Save, RotateCcw, Plus, Trash2, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { DatePickerIT } from "../components/DatePickerIT";
import { DeliveryCard, todayIso } from "../components/logistics/DeliveryInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const LEAD_LABELS = { 1: "AxB", 2: "AxC", 3: "AxD", 4: "AxE", 5: "AxF" };
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "mai");

const ImportCard = ({ title, subtitle, status, endpoint, accept, canManage, onDone, testid }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title}: importate ${r.data.count} righe (${r.data.caps} CAP)${r.data.skipped_named ? ` · ${r.data.skipped_named} righe cliente-specifiche ignorate` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 border-stone-200 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold text-stone-900">{title}</p>
          <p className="text-xs text-stone-500">{subtitle}</p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status?.count ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`${testid}-count`}>{status?.count ? `${status.count.toLocaleString("it-IT")} righe` : "non caricato"}</span>
      </div>
      <p className="text-xs text-stone-600">Ultimo import: <strong>{fmtDT(status?.imported_at)}</strong>{status?.filename ? ` · ${status.filename}` : ""}</p>
      {canManage && (
        <label className={`inline-flex items-center gap-2 text-xs font-semibold border border-dashed rounded-lg px-3 py-2 cursor-pointer w-fit ${busy ? "opacity-50 pointer-events-none" : "border-[#8A1C2A] text-[#8A1C2A] hover:bg-[#8A1C2A]/5"}`}>
          <Upload className="w-3.5 h-3.5" /> {busy ? "Import in corso…" : "Carica nuovo file"}
          <input type="file" accept={accept} className="hidden" data-testid={`${testid}-input`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      )}
    </Card>
  );
};

const LeadTimesTable = ({ canManage }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => axios.get(`${API}/logistics/lead-times`).then((r) => { setItems((r.data.items || []).map((x) => ({ ...x, provText: (x.province || []).join(", ") }))); setMeta(r.data); setDirty(false); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const upd = (i, patch) => { setItems((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x))); setDirty(true); };
  const toggleDay = (i, d) => { const cur = items[i].handover || []; upd(i, { handover: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort() }); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((x) => ({ filiale: x.filiale, regione: x.regione || "", province: String(x.provText || "").split(/[,\s;/-]+/).filter(Boolean), handover: x.handover || [], lead_days: Number(x.lead_days) || 1 }));
      await axios.put(`${API}/logistics/lead-times`, { items: payload });
      toast.success("Tempi di resa STEF salvati");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare la tabella tempi di resa STEF ai valori predefiniti (screenshot STEF)?")) return;
    await axios.post(`${API}/logistics/lead-times/reset`);
    toast.success("Tabella ripristinata");
    load();
  };
  return (
    <Card className="p-4 border-stone-200" data-testid="stef-lead-times-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <p className="font-bold text-stone-900">Tempi di resa STEF per provincia</p>
          <p className="text-xs text-stone-500">Partenza da Prato · giorni di affidamento e lead time per filiale. {meta.is_default ? "Valori predefiniti (dallo schema STEF)." : `Modificata il ${fmtDT(meta.updated_at)}.`}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setItems((p) => [...p, { filiale: "", regione: "", provText: "", handover: [0, 1, 2, 3, 4], lead_days: 1 }]); setDirty(true); }} className="h-8 border-stone-300" data-testid="lead-add-row"><Plus className="w-3.5 h-3.5 mr-1" /> Riga</Button>
            <Button size="sm" variant="outline" onClick={reset} className="h-8 border-stone-300" data-testid="lead-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Predefiniti</Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving} className="h-8 bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="lead-save"><Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Salvo…" : "Salva"}</Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[760px]" data-testid="lead-table">
          <thead className="bg-stone-50 text-stone-600">
            <tr><th className="text-left p-2">Filiale</th><th className="text-left p-2">Regione</th><th className="text-left p-2">Province</th><th className="text-left p-2">Affidamento</th><th className="text-left p-2">Lead</th>{canManage && <th />}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-stone-100" data-testid={`lead-row-${i}`}>
                <td className="p-1.5">{canManage ? <Input value={it.filiale} onChange={(e) => upd(i, { filiale: e.target.value })} className="h-7 text-xs bg-white" /> : <strong>{it.filiale}</strong>}</td>
                <td className="p-1.5">{canManage ? <Input value={it.regione || ""} onChange={(e) => upd(i, { regione: e.target.value })} className="h-7 text-xs bg-white" /> : it.regione}</td>
                <td className="p-1.5">{canManage ? <Input value={it.provText} onChange={(e) => upd(i, { provText: e.target.value })} className="h-7 text-xs bg-white font-mono" data-testid={`lead-prov-${i}`} /> : <span className="font-mono">{it.provText}</span>}</td>
                <td className="p-1.5">
                  <div className="flex gap-0.5">
                    {DAY_LABELS.slice(0, 6).map((d, di) => (
                      <button key={di} type="button" disabled={!canManage} onClick={() => toggleDay(i, di)} className={`w-8 h-6 rounded text-[10px] font-bold ${(it.handover || []).includes(di) ? "bg-sky-600 text-white" : "bg-stone-100 text-stone-400"} ${canManage ? "hover:opacity-80" : "cursor-default"}`} data-testid={`lead-day-${i}-${di}`}>{d}</button>
                    ))}
                  </div>
                </td>
                <td className="p-1.5">
                  {canManage ? (
                    <select value={it.lead_days} onChange={(e) => upd(i, { lead_days: Number(e.target.value) })} className="h-7 border border-stone-300 rounded bg-white px-1 text-xs font-mono" data-testid={`lead-days-${i}`}>
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{LEAD_LABELS[n]}</option>)}
                    </select>
                  ) : <span className={`font-mono font-bold ${it.lead_days === 1 ? "text-emerald-700" : it.lead_days === 2 ? "text-amber-700" : "text-red-700"}`}>{LEAD_LABELS[it.lead_days] || it.lead_days}</span>}
                </td>
                {canManage && <td className="p-1.5"><button type="button" onClick={() => { setItems((p) => p.filter((_, j) => j !== i)); setDirty(true); }} className="text-red-500 hover:text-red-700" title="Elimina riga" data-testid={`lead-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default function LogisticsPage({ canManage = false }) {
  const [status, setStatus] = useState(null);
  const [cap, setCap] = useState("");
  const [localita, setLocalita] = useState("");
  const [shipDate, setShipDate] = useState(todayIso());
  const [query, setQuery] = useState(null);
  const loadStatus = () => axios.get(`${API}/logistics/status`).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);
  const manage = canManage || status?.can_manage;
  const run = (e) => { e?.preventDefault?.(); if (cap.replace(/\D/g, "").length !== 5) { toast.error("Inserisci un CAP di 5 cifre"); return; } setQuery({ cap: cap.replace(/\D/g, ""), localita, shipDate }); };

  return (
    <div className="space-y-5" data-testid="logistics-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900 flex items-center gap-2"><Truck className="w-6 h-6 text-[#8A1C2A]" /> Logistica</h1>
          <p className="text-stone-500 text-sm">Chi consegna (BRT / STEF) e quando, in base al CAP e al giorno di spedizione da Prato.</p>
        </div>
      </div>

      <Card className="p-4 border-[#C5A059]/50 bg-gradient-to-r from-[#8A1C2A]/5 to-white" data-testid="logistics-lookup-card">
        <form onSubmit={run} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs">CAP *</Label>
            <Input value={cap} onChange={(e) => setCap(e.target.value)} placeholder="es. 50142" inputMode="numeric" maxLength={5} className="bg-white border-stone-300 font-mono" data-testid="logistics-cap-input" />
          </div>
          <div className="sm:col-span-4">
            <Label className="text-xs">Località (facoltativa)</Label>
            <Input value={localita} onChange={(e) => setLocalita(e.target.value)} placeholder="es. Firenze" className="bg-white border-stone-300" data-testid="logistics-localita-input" />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-xs">Data di spedizione</Label>
            <DatePickerIT value={shipDate} onChange={(v) => setShipDate(v || todayIso())} data-testid="logistics-ship-date" />
          </div>
          <div className="sm:col-span-3">
            <Button type="submit" className="w-full bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="logistics-search-btn"><Search className="w-4 h-4 mr-2" /> Calcola consegna</Button>
          </div>
        </form>
        <p className="text-[11px] text-stone-500 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> BRT consegna AxB nei CAP del suo cappario. STEF: se il giorno scelto non è di affidamento per quella zona si slitta al primo utile, si applica il lead time (AxB/AxC/AxD) e si arriva al primo giorno in cui il piano di trasporto passa nella località. Sabati, domeniche e festivi esclusi.</p>
      </Card>

      {query && <DeliveryCard cap={query.cap} localita={query.localita} shipDate={query.shipDate} simulate={7} onPickDate={(d) => { setShipDate(d); setQuery({ ...query, shipDate: d }); }} testid="logistics-result" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ImportCard title="Cappario BRT (Bartolini)" subtitle="Località raggiunte dal servizio fresh — file Excel mensile (CAP · Località · Provincia)" status={status?.brt} endpoint="/logistics/import/brt" accept=".xlsx,.xls" canManage={manage} onDone={loadStatus} testid="import-brt" />
        <ImportCard title="Piano di trasporto STEF" subtitle="File TFEITFR (località, CAP, filiale, giorni di passaggio Lun–Dom). Le righe cliente-specifiche vengono ignorate." status={status?.stef} endpoint="/logistics/import/stef" accept=".xls,.txt,.tsv,.csv" canManage={manage} onDone={loadStatus} testid="import-stef" />
      </div>

      <LeadTimesTable canManage={manage} />
    </div>
  );
}
